import { toast } from "sonner";
/* eslint-disable jsx-a11y/anchor-is-valid */
import * as React from "react";
import {useEffect, useState} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {compareBy, Order, stableSort} from "../../helpers/TableHelpers";
import {FilterCombobox} from "../common/FilterCombobox";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import JobRequestStatusChip from "../Jobs/JobRequestStatusChip";
import {JobRequestStatus, JobRequestStatusColors, JobRequestStatusIcons, JobRequestStatusLabels,} from "../../models/Jobs/JobRequestStatus";
import {DeleteProviderRequest, JobRequest,} from "../../models/Jobs/JobRequest";
import AbortJobModal from "../Jobs/AbortJobModal";
import JobsRepository from "../../repositories/JobsRepository";
import Fuse from "fuse.js";
import ConfirmMigrationEntitiesDeletionModal, {CONFIRM_MIGRATION_ENTITIES_DELETION_CONFIGS} from "../common/ConfirmMigrationEntitiesDeletionModal";
import {cn} from "@/lib/utils";
import {
  drawerFiltersBoxClassName,
  drawerFilterItemClassName,
  drawerSearchClassName,
  drawerTableWrapperClassName,
} from "../common/Table/TableStyles";
import {Table, TableHeader, TableBody, TableRow, TableHead, TableCell} from "@/components/ui/table";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {Search, Trash2, XCircle} from "lucide-react";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { BulkDeletionSummary } from "../common/BulkDeletionSummary";

interface DeleteProvidersTableProps {
  organizationId: string;
  newProvidersForDeletion: DeleteProviderRequest[];
  setNewProvidersForDeletion: React.Dispatch<
    React.SetStateAction<DeleteProviderRequest[]>
  >;
}

const DeleteProvidersTable: React.FC<DeleteProvidersTableProps> = ({
  organizationId,
  newProvidersForDeletion,
  setNewProvidersForDeletion,
}) => {
  const [order, setOrder] = React.useState<Order>("asc");
  const [sort, setSort] = React.useState<keyof JobRequest>("status");
  const [statusFilter, setStatusFilter] = React.useState<string | null>(null);
  const [searchForProviderNameId, setSearchForProviderNameId] = useState<string | null>("");
  const [abortJob, setAbortJob] = useState<JobRequest | null>(null);
  const [confirmDeletionModalOpen, setConfirmDeletionModalOpen] = useState<boolean>(false);
  const [providersDeletionStatus, setProvidersDeletionStatus] = useState<DeleteProviderRequest[] | null>(null);
  const jobsRepository = new JobsRepository();
  const queryClient = useQueryClient();

  const providersQuery = useQuery({ queryKey: [QUERY_KEYS.PROVIDERS_DELETION_STATUS, organizationId], queryFn:
    async () => {
      return await jobsRepository.getProvidersDeletionStatus(organizationId);
    }, staleTime: 5 * 60 * 1000,
      refetchInterval: 5 * 60 * 1000 + 1,
      refetchIntervalInBackground: true,
      refetchOnMount: true, });

  useEffect(() => {
    if (providersQuery.data) {
      setProvidersDeletionStatus(providersQuery.data);
    }
  }, [providersQuery]);

  const handleSearch = (newProviderNameId: string) => {
    setSearchForProviderNameId(newProviderNameId ?? null);
  };

  const changeSort = (newSort: keyof JobRequest) => {
    setSort(newSort);
  };

  const changeOrder = (newOrder: Order) => {
    setOrder(newOrder);
  };

  const { isError, isFetching } = providersQuery;
  const loading = isFetching || isError;

  const renderFilters = () => (
    <React.Fragment>
      <div className={drawerFilterItemClassName}>
        <Label className="text-sm">Status</Label>
        <FilterCombobox
          value={statusFilter}
          onChange={(newValue) => {
            setStatusFilter(newValue);
          }}
          placeholder="Status"
          options={Object.values(JobRequestStatus).map((status) => ({
            value: status,
            label: JobRequestStatusLabels[status as JobRequestStatus],
            color: JobRequestStatusColors[status as JobRequestStatus],
            icon: JobRequestStatusIcons[status as JobRequestStatus],
          }))}
        />
      </div>
    </React.Fragment>
  );

  const filteredJobs = (providersDeletionStatus || []).filter((job) => {
    return statusFilter == null || job.status === statusFilter;
  });

  const searchOptions = {
    keys: ["providerNameId"],
    includeScore: true,
    threshold: 0.1,
    providerSensitive: false,
  };

  const fuse = new Fuse(filteredJobs, searchOptions);
  const searchResults = searchForProviderNameId
    ? fuse.search(searchForProviderNameId).map((result) => result.item)
    : filteredJobs;
  const sortedJobs = sort
    ? stableSort<DeleteProviderRequest>(searchResults, compareBy(order, sort))
    : filteredJobs;
  const allRows = [...newProvidersForDeletion, ...sortedJobs];
  const newIds = new Set(newProvidersForDeletion.map((c) => c.migrationEntityId));

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      <div className={drawerFiltersBoxClassName}>
        <div className={drawerSearchClassName}>
          <Label className="text-sm">Search providers</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder="Name or ID"
              onChange={(event) => handleSearch(event.target.value)}
            />
          </div>
        </div>
        {renderFilters()}
      </div>
      <Button
        disabled={!(newProvidersForDeletion.length > 0)}
        onClick={() => setConfirmDeletionModalOpen(true)}
      >
        Delete Providers
      </Button>
      <div className={cn(drawerTableWrapperClassName, loading && "opacity-50 pointer-events-none")}>
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-0 text-center" />

              <TableHead>
                <SortableTableColumnHeader
                  label={"Name Id"}
                  sortProperty={"providerNameId"}
                  getSort={() => sort}
                  setSort={(newSort) =>
                    changeSort(newSort as keyof JobRequest)
                  }
                  getOrder={() => order}
                  setOrder={(newOrder) => changeOrder(newOrder)}
                />
              </TableHead>

              <TableHead>
                <SortableTableColumnHeader
                  label={"Status"}
                  sortProperty={"status"}
                  getSort={() => sort}
                  setSort={(newSort) =>
                    changeSort(newSort as keyof JobRequest)
                  }
                  getOrder={() => order}
                  setOrder={(newOrder) => changeOrder(newOrder)}
                />
              </TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>

          <TableBody>
            {allRows.map((deleteProviderJob) => {
              const canEdit =
                deleteProviderJob.status === JobRequestStatus.Pending ||
                deleteProviderJob.status === JobRequestStatus.New ||
                deleteProviderJob.status === JobRequestStatus.Completed;
              return (
                <TableRow
                  key={deleteProviderJob.id ?? deleteProviderJob.migrationEntityId}
                  className={newIds.has(deleteProviderJob.migrationEntityId) ? "bg-primary/10" : ""}
                >
                  <TableCell className="text-center" />
                  <TableCell>
                    <span className="text-sm">
                      {deleteProviderJob.providerNameId}
                      {deleteProviderJob.bulk && <BulkDeletionSummary bulk={deleteProviderJob.bulk} />}
                    </span>
                  </TableCell>
                  <TableCell>
                    <JobRequestStatusChip status={deleteProviderJob.status} />
                  </TableCell>
                  {canEdit ? (
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-destructive"
                        aria-label="Cancel"
                        onClick={() => {
                          deleteProviderJob.id
                            ? setAbortJob(deleteProviderJob)
                            : setNewProvidersForDeletion((prevRows) =>
                                prevRows.filter(
                                  (entity) =>
                                    entity.migrationEntityId !==
                                    deleteProviderJob.migrationEntityId
                                )
                              );
                        }}
                      >
                        {deleteProviderJob.status ===
                        JobRequestStatus.Completed ? (
                          <Trash2 className="size-4" />
                        ) : (
                          <XCircle className="size-4" />
                        )}
                      </Button>
                    </TableCell>
                  ) : (
                    <TableCell />
                  )}
                </TableRow>
              );
            })}
              {allRows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={99} className="h-24 text-center text-muted-foreground">
                    No pending deletions.
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
      </div>
      {abortJob ? (
        <AbortJobModal
          open={abortJob !== null}
          jobRequest={abortJob}
          onClose={() => {
            setAbortJob(null);
          }}
          onConfirm={async () => {
            await jobsRepository.deleteJob(abortJob.id);
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
            queryClient.invalidateQueries({ queryKey: [
              QUERY_KEYS.PROVIDERS_DELETION_STATUS,
              organizationId,
            ] });
            if (abortJob.status !== JobRequestStatus.Completed) {
              queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PROVIDERS, organizationId] });
            }
            setAbortJob(null);
          }}
        />
      ) : (
        <></>
      )}

      {confirmDeletionModalOpen ? (
        <ConfirmMigrationEntitiesDeletionModal
          open={confirmDeletionModalOpen}
          entityKeys={newProvidersForDeletion.map((p) => p.providerNameId)}
          confirmMigrationDeletionConfig={CONFIRM_MIGRATION_ENTITIES_DELETION_CONFIGS.PROVIDERS}
          onClose={() => {
            setConfirmDeletionModalOpen(false);
          }}
          onConfirm={async () => {
            await jobsRepository.startProvidersDeletion(
              organizationId,
              newProvidersForDeletion.map((c) => c.migrationEntityId)
            );
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
            queryClient.invalidateQueries({ queryKey: [
              QUERY_KEYS.PROVIDERS_DELETION_STATUS,
              organizationId,
            ] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PROVIDERS, organizationId] });
            setNewProvidersForDeletion([]);
            setConfirmDeletionModalOpen(false);
            toast.success("Deletion started");
          }}
        />
      ) : (
        <></>
      )}
    </div>
  );
};

export default DeleteProvidersTable;
